/**
 * Parses WA Growth Metrics from markdown content.
 *
 * Expected format per metric:
 *   ICON LABEL (vs Rolling 30-Day Avg) Total NUMBER (Daily Avg : NUMBER)
 *   W3Thu 48 🟢↑ / 34 | W3Wed 87 🟢↑ / 34 | ...
 *
 * Returns { success, summary, metrics, sources } or { success: false }.
 */

const METRIC_KEYS = [
  { match: /total tickets/i,      key: 'total-tickets',      label: 'Total Tickets',      icon: '🎟' },
  { match: /new customer/i,       key: 'new-customer',       label: 'New Customer',       icon: '🆕' },
  { match: /returning customer/i, key: 'returning-customer', label: 'Returning Customer', icon: '🔁' },
  { match: /sales hunt/i,         key: 'sales-hunt',         label: 'Sales Hunt',         icon: '🎯' },
  { match: /sales followup/i,     key: 'sales-followup',     label: 'Sales Followup',     icon: '📞' },
]

const HEADER_PATTERN = /Total\s+(\d[\d,]*)\s*\(Daily Avg\s*:\s*(\d[\d,]*)\)/

const DAY_ENTRY_PATTERN = /(W\d\w+)\s+(\d+)\s+([🟢🔴🟨][↑↓→])\s*\/\s*(\d+)/g

function parseTrend(emoji) {
  if (emoji.includes('🟢')) return 'up'
  if (emoji.includes('🔴')) return 'down'
  return 'flat'
}

function parseNum(s) {
  return parseInt(String(s).replace(/,/g, ''), 10) || 0
}

function parseDayEntries(line) {
  const days = []
  for (const m of line.matchAll(DAY_ENTRY_PATTERN)) {
    days.push({
      label: m[1],
      value: parseInt(m[2], 10),
      trend: parseTrend(m[3]),
      rollingAvg: parseInt(m[4], 10),
    })
  }
  return days
}

export function parseWAGrowth(raw) {
  if (!raw) return { success: false }

  const lines = raw.split('\n')
  const parsed = {}

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    for (const metric of METRIC_KEYS) {
      if (parsed[metric.key]) continue
      if (!metric.match.test(line)) continue

      const headerMatch = line.match(HEADER_PATTERN)
      if (!headerMatch) continue

      // Daily entries may be on the same line or the next line
      let days = parseDayEntries(line)
      if (days.length === 0 && i + 1 < lines.length) {
        days = parseDayEntries(lines[i + 1])
      }

      parsed[metric.key] = {
        key: metric.key,
        icon: metric.icon,
        label: metric.label,
        total: parseNum(headerMatch[1]),
        dailyAvg: parseNum(headerMatch[2]),
        days,
      }
      break
    }
  }

  // Must have Total Tickets to build the dashboard
  if (!parsed['total-tickets']) return { success: false }

  const summary = parsed['total-tickets']

  const metrics = METRIC_KEYS
    .filter(m => parsed[m.key])
    .map(m => parsed[m.key])

  const sources = metrics.filter(m => m.key !== 'total-tickets')

  return { success: true, summary, metrics, sources }
}
